/**
 * @tanstack/react-query hooks for the POSH module (Phase 3.3a:
 * training + certificates). Thin wrappers over lib/api/posh.ts so the
 * pages stay declarative and caching/invalidation lives in one place.
 *
 * Query-key convention: ["posh", <area>, ...params] so a single
 * `queryClient.invalidateQueries({ queryKey: ["posh"] })` can flush the
 * whole module, and 3.3b can extend with ["posh","complaints"] etc.
 * without colliding.
 *
 * The shared QueryClient (components/providers.tsx) already sets
 * retry: 1, refetchOnWindowFocus: false, staleTime: 30s. Global
 * content (modules/scenarios/scenes/quiz) rarely changes, so it gets a
 * longer staleTime; per-user progress/certs use the default.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { poshApi } from "@/lib/api";
import type { ApiError } from "@/types/auth";
import type {
  AssignICCRequest,
  SetComplaintRespondentRequest,
  CreateAwarenessEventRequest,
  CreateICCMemberRequest,
  CreateICCMinutesRequest,
  DecideAppealRequest,
  FileAppealRequest,
  FileComplaintRequest,
  GeneratePolicyRequest,
  GenerateReportRequest,
  ListModulesResponse,
  PublishPolicyRequest,
  RecordEmployerActionRequest,
  RecordFindingsRequest,
  RecordInterimReliefRequest,
  RemindTrainingRequest,
  SubmitQuizRequest,
  UpdateComplaintStatusRequest,
  UpdateICCMemberRequest,
  UpdateInterimReliefRequest,
  UpdatePOSHTrainingSettingsRequest,
  UpdatePolicyRequest,
  UpdateProgressRequest,
  UpdateReportRequest,
  UpdateReportStatusRequest,
} from "@/types/posh";

const STATIC_CONTENT_STALE = 5 * 60 * 1000; // 5 min — global curriculum

export const poshKeys = {
  all: ["posh"] as const,
  modules: () => [...poshKeys.all, "modules"] as const,
  scenarios: (moduleId: number) =>
    [...poshKeys.all, "scenarios", moduleId] as const,
  scenes: (scenarioId: number) =>
    [...poshKeys.all, "scenes", scenarioId] as const,
  quiz: (moduleId: number) => [...poshKeys.all, "quiz", moduleId] as const,
  certificates: () => [...poshKeys.all, "certificates"] as const,
  compliance: () => [...poshKeys.all, "compliance"] as const,
  trainingSettings: () => [...poshKeys.all, "training-settings"] as const,
  // --- 3.3b -----------------------------------------------------------
  myComplaints: (status?: string) =>
    [...poshKeys.all, "my-complaints", status ?? "all"] as const,
  myComplaint: (id: number) =>
    [...poshKeys.all, "my-complaint", id] as const,
  complaints: (status?: string) =>
    [...poshKeys.all, "complaints", status ?? "all"] as const,
  complaint: (id: number) => [...poshKeys.all, "complaint", id] as const,
  committee: () => [...poshKeys.all, "committee"] as const,
  iccMembers: () => [...poshKeys.all, "icc-members"] as const,
  iccComposition: () => [...poshKeys.all, "icc-composition"] as const,
  iccMinutes: (complaintId?: number) =>
    [...poshKeys.all, "icc-minutes", complaintId ?? "all"] as const,
  policyTemplates: () => [...poshKeys.all, "policy-templates"] as const,
  policies: (status?: string) =>
    [...poshKeys.all, "policies", status ?? "all"] as const,
  policy: (id: number) => [...poshKeys.all, "policy", id] as const,
  reports: (status?: string) =>
    [...poshKeys.all, "reports", status ?? "all"] as const,
  report: (id: number) => [...poshKeys.all, "report", id] as const,
  awarenessEvents: () => [...poshKeys.all, "awareness-events"] as const,
};

// ---------------------------------------------------------------------------
// Strict-2FA detection.
//
// The ICC/admin complaint reads + every /icc route sit behind the
// backend's RequireTOTPEnrolledAndVerified. A caller who is NOT
// TOTP-enrolled is hard-403'd there with { error: "totp_enrollment_
// required" } (NOT waved through, NOT a generic error). /me cannot
// distinguish a TOTP-pending session (deferred H3), so the ONLY reliable
// signal is this specific 403 from these endpoints. The pages branch on
// is2FARequiredError(query.error) to render the "Two-factor required"
// state with a /2fa-setup link, instead of an error toast / dead spinner.
//
// upgrade_required (tenant lacks the posh feature) is handled separately
// by the api client (hard redirect to /pricing) so it never reaches here.
// ---------------------------------------------------------------------------

const TOTP_REQUIRED_ERRORS = new Set([
  "totp_enrollment_required",
  "totp_required",
  "totp_not_enrolled",
]);

export function is2FARequiredError(err: unknown): boolean {
  const resp = (err as AxiosError<ApiError> | undefined)?.response;
  if (resp?.status !== 403) return false;
  const code = resp.data?.error;
  return typeof code === "string" && TOTP_REQUIRED_ERRORS.has(code);
}

// react-query should not burn its retry on a deterministic 2FA-required
// 403 — it will never succeed without enrolment. Same for the other
// non-retriable 4xx. Used as the `retry` predicate on the 2FA-gated
// queries so the "2FA required" state appears immediately.
function retryUnlessClientError(failureCount: number, err: unknown): boolean {
  if (is2FARequiredError(err)) return false;
  const status = (err as AxiosError | undefined)?.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

// --- Training ----------------------------------------------------------

export function useTrainingModules() {
  return useQuery({
    queryKey: poshKeys.modules(),
    queryFn: ({ signal }) => poshApi.listModules(signal),
  });
}

export function useScenarios(moduleId: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.scenarios(moduleId),
    queryFn: ({ signal }) => poshApi.listScenarios(moduleId, signal),
    enabled: enabled && Number.isFinite(moduleId) && moduleId > 0,
    staleTime: STATIC_CONTENT_STALE,
  });
}

export function useScenes(scenarioId: number | undefined) {
  return useQuery({
    queryKey: poshKeys.scenes(scenarioId ?? -1),
    queryFn: ({ signal }) => poshApi.listScenes(scenarioId as number, signal),
    enabled:
      scenarioId !== undefined &&
      Number.isFinite(scenarioId) &&
      scenarioId > 0,
    staleTime: STATIC_CONTENT_STALE,
  });
}

export function useQuiz(moduleId: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.quiz(moduleId),
    queryFn: ({ signal }) => poshApi.getQuiz(moduleId, signal),
    enabled: enabled && Number.isFinite(moduleId) && moduleId > 0,
    staleTime: STATIC_CONTENT_STALE,
  });
}

/**
 * PUT /posh/training/progress. On success the (tenant,user,module)
 * progress changed, so the modules list (which embeds per-module
 * progress) is invalidated to reflect the new % / status / cert badge.
 */
export function useUpdateProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProgressRequest) =>
      poshApi.updateProgress(body),
    onSuccess: (data) => {
      // Patch the saved row into the cached list at once, so the quiz
      // gate (which reads it) is right the moment the player navigates
      // there — then refetch for the server's full view.
      const p = data?.progress;
      if (p) {
        qc.setQueryData<ListModulesResponse>(poshKeys.modules(), (old) =>
          old
            ? {
                ...old,
                modules: old.modules.map((m) =>
                  m.module.id === p.module_id ? { ...m, progress: p } : m,
                ),
              }
            : old,
        );
      }
      void qc.invalidateQueries({ queryKey: poshKeys.modules() });
    },
  });
}

/**
 * POST /posh/training/modules/{id}/quiz/submit. A pass marks the module
 * completed and may issue a certificate, so both the modules list and
 * the certificates list are invalidated.
 */
export function useSubmitQuiz(moduleId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitQuizRequest) =>
      poshApi.submitQuiz(moduleId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: poshKeys.modules() });
      if (data.passed) {
        void qc.invalidateQueries({ queryKey: poshKeys.certificates() });
      }
    },
    // A 409 scenarios_incomplete means our cached progress is stale —
    // refetch so the quiz gate shows the truth.
    onError: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.modules() });
    },
  });
}

// --- Certificates ------------------------------------------------------

export function useCertificates() {
  return useQuery({
    queryKey: poshKeys.certificates(),
    queryFn: ({ signal }) => poshApi.listCertificates(signal),
  });
}

// --- Compliance dashboard (business review P1.2) -----------------------

/**
 * GET /posh/training/compliance — the org-wide training/certification
 * roster. RBAC company_admin|hr_manager is enforced server-side; the
 * page hard role-gates so this is not fired for a non-admin. A 403 here
 * (defence in depth — e.g. a stale role) is a deterministic client
 * error that can never succeed on retry, so retryUnlessClientError
 * short-circuits it and the page renders the "no access" / error state
 * at once rather than spinning through a retry.
 */
export function useCompliance(enabled = true) {
  return useQuery({
    queryKey: poshKeys.compliance(),
    queryFn: ({ signal }) => poshApi.getCompliance(signal),
    retry: retryUnlessClientError,
    // `enabled` lets a secondary consumer (the Run-overview "needs your
    // attention" strip) defer/skip the fetch for non-HR / non-posh
    // callers instead of firing a guaranteed 403.
    enabled,
  });
}

/**
 * POST /posh/training/remind — nudge one non-compliant employee
 * ({ user_id }) or all of them ({ all: true }). HR-admin gated + CSRF
 * server-side; 24h server-side dedup (the response's `skipped` count).
 * Sending email doesn't change compliance state, so no invalidation.
 */
export function useRemindTraining() {
  return useMutation({
    mutationFn: (body: RemindTrainingRequest) => poshApi.remindTraining(body),
  });
}

/**
 * GET /posh/training/settings — the annual refresher period (migration
 * 125). HR-admin gated server-side; `enabled` lets the page skip the
 * fetch for a non-admin.
 */
export function useTrainingSettings(enabled = true) {
  return useQuery({
    queryKey: poshKeys.trainingSettings(),
    queryFn: ({ signal }) => poshApi.getTrainingSettings(signal),
    retry: retryUnlessClientError,
    enabled,
  });
}

/**
 * PATCH /posh/training/settings. The period moves every employee's
 * "certified until" date, so the compliance roster is refetched too.
 */
export function useUpdateTrainingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePOSHTrainingSettingsRequest) =>
      poshApi.updateTrainingSettings(body),
    onSuccess: (data) => {
      qc.setQueryData(poshKeys.trainingSettings(), data);
      void qc.invalidateQueries({ queryKey: poshKeys.compliance() });
    },
  });
}

// ===========================================================================
// 3.3b — Complaints / ICC / Policy / Annual reports
// ===========================================================================

// --- Complaints: own (no 2FA) ------------------------------------------

export function useMyComplaints(status?: string) {
  return useQuery({
    queryKey: poshKeys.myComplaints(status),
    queryFn: ({ signal }) =>
      poshApi.listMyComplaints(status ? { status } : undefined, signal),
  });
}

export function useMyComplaint(id: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.myComplaint(id),
    queryFn: ({ signal }) => poshApi.getMyComplaint(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

/**
 * Who sits on the Internal Committee today + whether this viewer can see
 * the committee register (GET /posh/committee — every employee, no 2FA).
 */
export function useCommittee(enabled = true) {
  return useQuery({
    queryKey: poshKeys.committee(),
    queryFn: ({ signal }) => poshApi.getCommittee(signal),
    staleTime: STATIC_CONTENT_STALE,
    enabled,
  });
}

/**
 * The complainant attaches evidence to their OWN complaint (no 2FA).
 * Refreshes the own detail so the new document is listed.
 */
export function useUploadMyComplaintDocument(complaintId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      poshApi.uploadMyComplaintDocument(complaintId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.myComplaint(complaintId) });
    },
  });
}

/**
 * File a complaint (own path — NOT 2FA-gated). Invalidates the
 * complainant's own list so the new case shows immediately.
 */
export function useFileComplaint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FileComplaintRequest) =>
      poshApi.fileComplaint(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.all });
    },
  });
}

// --- Complaints: ICC/admin (STRICT 2FA) --------------------------------
//
// These queries DON'T retry a 2FA-required 403 (it can never succeed
// without enrolment) so the page can render the "2FA required" state at
// once. Callers inspect query.error with is2FARequiredError().

export function useComplaints(status?: string) {
  return useQuery({
    queryKey: poshKeys.complaints(status),
    queryFn: ({ signal }) =>
      poshApi.listComplaints(status ? { status } : undefined, signal),
    retry: retryUnlessClientError,
  });
}

export function useComplaint(id: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.complaint(id),
    queryFn: ({ signal }) => poshApi.getComplaint(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
    retry: retryUnlessClientError,
  });
}

export function useUpdateComplaintStatus(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateComplaintStatusRequest) =>
      poshApi.updateComplaintStatus(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.complaint(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "complaints"],
      });
    },
  });
}

export function useAssignComplaintICC(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignICCRequest) =>
      poshApi.assignComplaintICC(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.complaint(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "complaints"],
      });
    },
  });
}

/** PUT /posh/complaints/{id}/respondent — the committee links (or
 * unlinks) the respondent's account. */
export function useSetComplaintRespondent(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetComplaintRespondentRequest) =>
      poshApi.setComplaintRespondent(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.complaint(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "complaints"],
      });
    },
  });
}

export function useUploadComplaintDocument(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      file: File;
      documentType?: string;
      description?: string;
    }) =>
      poshApi.uploadComplaintDocument(id, vars.file, {
        documentType: vars.documentType,
        description: vars.description,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.complaint(id) });
    },
  });
}

// --- Inquiry lifecycle: §12 interim reliefs / §13 findings + §13(4)
//     employer action / §18 appeals (migration 090, STRICT 2FA).
//
// The §12/§13/§18 records are embedded on the ICC/admin complaint detail
// (GET /posh/complaints/{id}), so EVERY mutation below invalidates that
// detail query (and the register) so the collapsible sections re-render
// the new record + timeline event without a manual refresh — the same
// invalidation shape as useUpdateComplaintStatus.

function invalidateComplaint(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
) {
  void qc.invalidateQueries({ queryKey: poshKeys.complaint(id) });
  void qc.invalidateQueries({ queryKey: [...poshKeys.all, "complaints"] });
}

export function useRecordInterimRelief(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordInterimReliefRequest) =>
      poshApi.recordInterimRelief(id, body),
    onSuccess: () => invalidateComplaint(qc, id),
  });
}

export function useUpdateInterimRelief(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reliefId: number; body: UpdateInterimReliefRequest }) =>
      poshApi.updateInterimRelief(id, vars.reliefId, vars.body),
    onSuccess: () => invalidateComplaint(qc, id),
  });
}

export function useRecordFindings(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordFindingsRequest) =>
      poshApi.recordFindings(id, body),
    onSuccess: () => invalidateComplaint(qc, id),
  });
}

export function useRecordEmployerAction(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordEmployerActionRequest) =>
      poshApi.recordEmployerAction(id, body),
    onSuccess: () => invalidateComplaint(qc, id),
  });
}

export function useFileAppeal(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FileAppealRequest) => poshApi.fileAppeal(id, body),
    onSuccess: () => invalidateComplaint(qc, id),
  });
}

export function useDecideAppeal(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { appealId: number; body: DecideAppealRequest }) =>
      poshApi.decideAppeal(id, vars.appealId, vars.body),
    onSuccess: () => invalidateComplaint(qc, id),
  });
}

// --- ICC members (STRICT 2FA) ------------------------------------------

export function useICCMembers(enabled = true) {
  return useQuery({
    queryKey: poshKeys.iccMembers(),
    queryFn: ({ signal }) => poshApi.listICCMembers(signal),
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useCreateICCMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateICCMemberRequest) =>
      poshApi.createICCMember(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.iccMembers() });
      // §4 composition state depends on seat existence/role/gender,
      // so a new seat must refresh the health card too.
      void qc.invalidateQueries({ queryKey: poshKeys.iccComposition() });
    },
  });
}

export function useUpdateICCMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateICCMemberRequest }) =>
      poshApi.updateICCMember(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.iccMembers() });
      void qc.invalidateQueries({ queryKey: poshKeys.iccComposition() });
    },
  });
}

export function useDeactivateICCMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => poshApi.deactivateICCMember(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.iccMembers() });
      void qc.invalidateQueries({ queryKey: poshKeys.iccComposition() });
    },
  });
}

/**
 * GET /posh/icc/composition — POSH Act §4 composition health snapshot.
 * RBAC company_admin|hr_manager enforced server-side; the page hard
 * role-gates so this is not fired for a non-admin. A 403 here (defence
 * in depth — e.g. a stale role) is a deterministic client error that
 * cannot succeed on retry, so retryUnlessClientError short-circuits it
 * and the page renders the "no access" state at once.
 *
 * NOTE: this is the same access shape as useCompliance() (the sibling
 * P1.2 endpoint), NOT the strict-2FA pattern — composition warnings
 * must be visible even to an HR admin who hasn't enrolled in TOTP.
 */
export function useICCComposition() {
  return useQuery({
    queryKey: poshKeys.iccComposition(),
    queryFn: ({ signal }) => poshApi.getICCComposition(signal),
    retry: retryUnlessClientError,
  });
}

// --- IC meeting minutes (STRICT 2FA) -----------------------------------

/**
 * GET /posh/icc/minutes — the committee's Rule 7 minutes records. Same
 * strict-2FA posture as the ICC member/complaint reads; a non-enrolled
 * caller is hard-403'd, so callers branch on is2FARequiredError(query.error).
 * An optional complaintId narrows the list to one inquiry's minutes.
 */
export function useICCMinutes(complaintId?: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.iccMinutes(complaintId),
    queryFn: ({ signal }) => poshApi.listICCMinutes(complaintId, signal),
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useCreateICCMinutes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateICCMinutesRequest) =>
      poshApi.createICCMinutes(body),
    onSuccess: () => {
      // Flush every minutes list (the all-list + any complaint-filtered).
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "icc-minutes"],
      });
    },
  });
}

// --- Policy (NOT 2FA-gated; RBAC enforced server-side on mutations) ----

export function usePolicyTemplates() {
  return useQuery({
    queryKey: poshKeys.policyTemplates(),
    queryFn: ({ signal }) => poshApi.listPolicyTemplates(signal),
    staleTime: STATIC_CONTENT_STALE,
  });
}

export function usePolicies(status?: string) {
  return useQuery({
    queryKey: poshKeys.policies(status),
    queryFn: ({ signal }) =>
      poshApi.listPolicies(status ? { status } : undefined, signal),
  });
}

export function usePolicy(id: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.policy(id),
    queryFn: ({ signal }) => poshApi.getPolicy(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useGeneratePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneratePolicyRequest) =>
      poshApi.generatePolicy(body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "policies"],
      });
    },
  });
}

export function useUpdatePolicy(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePolicyRequest) =>
      poshApi.updatePolicy(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.policy(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "policies"],
      });
    },
  });
}

export function usePublishPolicy(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: PublishPolicyRequest) =>
      poshApi.publishPolicy(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.policy(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "policies"],
      });
    },
  });
}

// --- Annual reports (RBAC enforced server-side on mutations) ------------

export function useReports(status?: string) {
  return useQuery({
    queryKey: poshKeys.reports(status),
    queryFn: ({ signal }) =>
      poshApi.listReports(status ? { status } : undefined, signal),
  });
}

export function useReport(id: number, enabled = true) {
  return useQuery({
    queryKey: poshKeys.report(id),
    queryFn: ({ signal }) => poshApi.getReport(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateReportRequest) =>
      poshApi.generateReport(body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "reports"],
      });
    },
  });
}

export function useUpdateReport(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateReportRequest) =>
      poshApi.updateReport(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.report(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "reports"],
      });
    },
  });
}

export function useUpdateReportStatus(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateReportStatusRequest) =>
      poshApi.updateReportStatus(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.report(id) });
      void qc.invalidateQueries({
        queryKey: [...poshKeys.all, "reports"],
      });
    },
  });
}

// --- §19(c) awareness event register (CR0.1) ----------------------------
//
// HR-admin RBAC enforced server-side (company_admin | hr_manager); the
// surface hard role-gates via usePoshAdmin so the query never fires for a
// non-admin. NOT 2FA-gated (title + date + attendee count — no PII).

export function useAwarenessEvents(enabled = true) {
  return useQuery({
    queryKey: poshKeys.awarenessEvents(),
    queryFn: ({ signal }) => poshApi.listAwarenessEvents(signal),
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useCreateAwarenessEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAwarenessEventRequest) =>
      poshApi.createAwarenessEvent(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.awarenessEvents() });
    },
  });
}

export function useDeleteAwarenessEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => poshApi.deleteAwarenessEvent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poshKeys.awarenessEvents() });
    },
  });
}
